import { useQuery } from "@apollo/client/react";
import { use } from "react";
import {
  Outlet,
  Navigate,
  createBrowserRouter,
  RouterProvider,
  useLocation,
} from "react-router";

import { LocalQuery } from "./cache";
import { AuthContainer } from "./containers/pages/auth/AuthContainerTmp";
import { LogoutCompleted } from "./components/pages/auth/LogoutCompleted";
import { ErrorPage } from "./components/pages/ErrorPage";
import { NotFoundPage } from "./components/pages/NotFoundPage";
import { Header } from "./containers/organisms/Header";
import { MenuList } from "./containers/organisms/MenuList";

import { Snackbar } from "./containers/organisms/Snackbar";
import { GET_APP_STATE } from "./graphql/queries";
import { useHandleServerError } from "./hooks";
import { MedicalInstitutionPage } from "./containers/pages/medicalInstitution/MedicalInstitutionPage";
import { MedicalInstitutionDetailPage } from "./containers/pages/medicalInstitution/MedicalInstitutionDetailPage";
import { ResendWelcomeMailPage } from "./containers/pages/medicalInstitution/ResendWelcomeMailPage";
import { ResendWelcomeMailV2Page } from "./containers/pages/medicalInstitution/ResendWelcomeMailV2Page";
import { PatientScPage } from "./containers/pages/patientSc/PatientScPage";
import { PatientScDetailPage } from "./containers/pages/patientSc/PatientScDetailPage";
import { PatientHtPage } from "./containers/pages/patientHt/PatientHtPage";
import { PatientHtDetailPage } from "./containers/pages/patientHt/PatientHtDetailPage";
import { UpdateEmailAddressPage } from "./containers/pages/patientHt/UpdateEmailAddressPage";
import { PublishVerifyCodePage } from "./containers/pages/patientHt/PublishVerifyCodePage";
import { DeliveryOrderPage } from "./containers/pages/deliveryOrder/DeliveryOrderPage";
import { DeliveryOrderDetailPage } from "./containers/pages/deliveryOrder/DeliveryOrderDetailPage";
import { useAuthentication, AuthContext } from "./hooks/useAuthentication";
import { CreateJsonDeliveryOrderPage } from "./containers/pages/deliveryOrder/CreateJsonDeliveryOrderPage";
import { PrescriptionCancelPage } from "./containers/pages/prescription/PrescriptionCancelPage";
import { PrescriptionCancelErrorPage } from "./containers/pages/prescription/PrescriptionCancelErrorPage";
import { UserRole, USER_ROLE } from "../src/utils/constants";
import { PatientHaudyPage } from "./containers/pages/patientHaudy/PatientHaudyPage";
import { PatientHaudyDetailPage } from "./containers/pages/patientHaudy/PatientHaudyDetailPage";
import { NotificationCreatePage } from "./containers/pages/notification/NotificationCreatePage";
import { NotificationListPage } from "./containers/pages/notification/NotificationListPage";
import { NotificationDetailPage } from "./containers/pages/notification/NotificationDetailPage";
import { NotificationEditPage } from "./containers/pages/notification/NotificationEditPage";
// TODO: ファイルアップロード機能でアップロードファイルが空になる問題が解消されたら表示する
// import { PdtPage } from "./containers/pages/pdt/PdtPage";
// import { PdtDetailPage } from "./containers/pages/pdt/PdtDetailPage";
// import { UploadPdtDocumentPage } from "./containers/pages/pdt/document/UploadPdtDocumentPage";
// import { CreatePdtDocumentPage } from "./containers/pages/pdt/document/CreatePdtDocumentPage";

function Root() {
  const { auth } = use(AuthContext);
  const { data: appData } = useQuery<LocalQuery>(GET_APP_STATE);

  const { isHeaderVisible, isMenuListVisible, isNotSameSubDomain } = appData
    ? appData.appState
    : {
        isHeaderVisible: true,
        isMenuListVisible: true,
        isNotSameSubDomain: false,
      };

  const isLoggedIn = auth.isLoggedIn;

  return (
    <>
      {/* FIXME: isNotSameSubDomainの関係で見通しが悪いので、修正したい */}
      {isHeaderVisible && !isNotSameSubDomain && <Header />}
      {isMenuListVisible && isLoggedIn && !isNotSameSubDomain && (
        <MenuList onCreatePrescriptionClick={() => undefined} />
      )}
      {isLoggedIn && (
        <>
          {/* <CreatePrescriptionModal /> */}
          {/* <PrescriptionCompletedModal /> */}
        </>
      )}
      <Outlet />
      <Snackbar />
    </>
  );
}

function Private() {
  useHandleServerError();

  const location = useLocation();

  const { auth, loading } = use(AuthContext);

  const { data: appData, loading: appDataLoading } =
    useQuery<LocalQuery>(GET_APP_STATE);
  const isSessionExpired = auth.sessionExpired || false;
  const { isNotFound, isForbidden, isServerError } = appData
    ? appData.appState
    : { isNotFound: false, isForbidden: false, isServerError: false };
  const isLoggedIn = auth.isLoggedIn;

  if (loading || appDataLoading) {
    return null;
  }

  if (isForbidden || isNotFound) return <NotFoundPage />;
  if (isServerError) return <ErrorPage />;
  if (!isLoggedIn && !isSessionExpired) {
    return <Navigate to="/login" replace />;
  }

  const userRole = (auth.userRole as UserRole) || USER_ROLE.SS_USER;
  if (isLoggedIn && !isSessionExpired && location.pathname === "/") {
    const to =
      userRole === USER_ROLE.CS_USER ? "/patient" : "/medicalInstitution";
    return <Navigate to={to} replace />;
  }

  return <Outlet />;
}

const router = createBrowserRouter([
  {
    path: "/",
    element: <Root />,
    children: [
      {
        path: "/",
        element: <Private />,
        children: [
          {
            path: "/deliveryOrder/create",
            element: <CreateJsonDeliveryOrderPage />,
          },
          {
            path: "/deliveryOrder/:id",
            element: <DeliveryOrderDetailPage />,
          },
          {
            path: "/deliveryOrder",
            element: <DeliveryOrderPage />,
          },
          {
            path: "/medicalInstitution",
            element: <MedicalInstitutionPage />,
          },
          {
            path: "/medicalInstitution/:id/contracts/:contractId/resendWelcomeMail",
            element: <ResendWelcomeMailPage />,
          },
          {
            path: "/medicalInstitution/:id/contracts/:contractId/resendWelcomeMailV2",
            element: <ResendWelcomeMailV2Page />,
          },
          {
            path: "/medicalInstitution/:id",
            element: <MedicalInstitutionDetailPage />,
          },
          {
            path: "/patient",
            element: <PatientScPage />,
          },
          {
            path: "/patient/:id",
            element: <PatientScDetailPage />,
          },
          {
            path: "/patient_ht",
            element: <PatientHtPage />,
          },
          {
            path: "/patient_ht/:id/updateEmailAddress",
            element: <UpdateEmailAddressPage />,
          },
          {
            path: "/patient_ht/:id/publish-verify-code",
            element: <PublishVerifyCodePage />,
          },
          {
            path: "/patient_ht/:id",
            element: <PatientHtDetailPage />,
          },
          {
            path: "/patient_haudy",
            element: <PatientHaudyPage />,
          },
          {
            path: "/patient_haudy/:id",
            element: <PatientHaudyDetailPage />,
          },
          {
            path: "/notifications/create",
            element: <NotificationCreatePage />,
          },
          {
            path: "/notifications/:id/edit",
            element: <NotificationEditPage />,
          },
          {
            path: "/notifications/:id",
            element: <NotificationDetailPage />,
          },
          {
            path: "/notifications",
            element: <NotificationListPage />,
          },
          {
            path: "/cancelPrescription/error",
            element: <PrescriptionCancelErrorPage />,
          },
          {
            path: "/cancelPrescription",
            element: <PrescriptionCancelPage />,
          },
        ],
      },
      {
        path: "/login",
        element: <AuthContainer />,
      },
      {
        path: "/logout",
        element: <LogoutCompleted />,
      },
      {
        path: "*",
        element: <NotFoundPage />,
      },
    ],
  },
]);

export function Router() {
  const { auth, loading, logout, expiresSession } = useAuthentication();
  return (
    <AuthContext value={{ auth, loading, logout, expiresSession }}>
      <RouterProvider router={router} />
    </AuthContext>
  );
}
